<script setup lang="ts">
	import dayjs from 'dayjs'

	const props = defineProps<{
		invoiceDetails: InvoiceContext | null
	}>()

	const { currencyToNaira } = useCurrency()
	useHead({
		link: [
			{
				href: 'https://mcg-business.vercel.app/css/fonts.css',
				rel: 'stylesheet',
				crossorigin: 'anonymous'
			}
		]
	})

	type OptionsFormat = Record<
		string,
		{
			value: string
			wrapperStyle?: string
			titleStyle?: string
			valueStyle?: string
		}
	>

	const invoiceStatus = computed(() => props.invoiceDetails?.invoice?.status)

	const companyInfo = computed(() => [
		{
			label: props.invoiceDetails?.company?.email || 'N/A',
			icon: {
				name: 'mail',
				dir: 'communication'
			}
		},
		{
			label: props.invoiceDetails?.company?.phone || 'N/A',
			icon: {
				name: 'phone',
				dir: 'communication'
			}
		},
		{
			label: props.invoiceDetails?.company?.address || 'N/A',
			icon: {
				name: 'address',
				dir: 'line'
			}
		}
	])
	const billingInfo = computed(() => [
		{
			label: props.invoiceDetails?.invoice?.customer?.user?.phone || 'N/A',
			icon: {
				name: 'phone',
				dir: 'communication'
			}
		},
		{
			label: props.invoiceDetails?.invoice?.customer?.email || 'N/A',
			icon: {
				name: 'mail',
				dir: 'communication'
			}
		},
		{
			label: props.invoiceDetails?.invoice?.customer?.address || 'N/A',
			icon: {
				name: 'address',
				dir: 'line'
			}
		}
	])
	const invoiceInfo = computed<OptionsFormat>(() => ({
		'Invoice ID.:': {
			value: props.invoiceDetails?.invoice?.id || 'N/A'
		},
		'Issue Date:': {
			value: dayjs(props.invoiceDetails?.invoice?.created_at).format('DD MMM, YYYY') || 'N/A'
		},
		'Payment Due:': {
			value: dayjs(props.invoiceDetails?.invoice?.due_date).format('DD MMM, YYYY') || 'N/A'
		},
		'Amount Due:': {
			value: currencyToNaira(props.invoiceDetails?.invoice?.amount || 0),
			wrapperStyle: 'min-height: 18.46px; background-color:#F2F4F7;  align-items: center',
			titleStyle: 'color:#475467; font-weight: 600; font-size: 12px; line-height:12.31px;',
			valueStyle: 'font-weight: 600; font-size: 12px; color:#12B76A;'
		}
	}))

	const paymentOption = computed<OptionsFormat>(() => ({
		'Bank Name:': {
			value: props.invoiceDetails?.payment_details?.bank || 'N/A'
		},
		'Account No.:': {
			value: props.invoiceDetails?.payment_details?.account_number || 'N/A'
		},
		'Account Name:': {
			value: props.invoiceDetails?.payment_details?.account_name || 'N/A'
		}
	}))
	const termsAndConditions = computed(() => [
		`Payment is due in ${props.invoiceDetails?.due_in || 0}`,
		`Please make payment to  ${props.invoiceDetails?.payment_details?.account_name || 0}`
	])
	const tableColumn = ['Product', 'Quantity', 'Unit Cost', 'Amount']
</script>

<template>
	<div class="w-full">
		<div
			:style="{
				border: '1px solid #EAECF0',
				minHeight: '647.73px',
				height: '100%',
				borderRadius: '0.375rem',
				flex: '1 1 0%',
				paddingInline: '2rem',
				paddingBlock: '3rem',
				position: 'relative',
				backgroundColor: 'white'
			}"
		>
			<table>
				<tbody>
					<tr>
						<td style="width: 70%; vertical-align: baseline" class="sm:!w-[59%]">
							<lazy-tools-icon
								dir="other"
								icon="logo"
								:height="27.71"
								:width="103.86"
								class="center mb-auto block"
							/>
						</td>

						<td class="w-full">
							<ul class="relative" style="width: 100%">
								<li style="margin-bottom: 12px">
									<h5
										style="
											font-weight: 600;
											color: #094063;
											font-size: 0.875rem;
											line-height: 1.25rem;
										"
									>
										MYCOVERGENIUS LIMITED
									</h5>
								</li>
								<li
									v-for="(value, index) in companyInfo"
									:key="index"
									style="
										display: flex;
										align-items: center;
										max-width: 140px;
										column-gap: 0.5rem;
										padding-bottom: 0.625rem;
									"
								>
									<lazy-tools-icon
										:dir="value.icon.dir"
										:icon="value.icon.name"
										stroke="#475467"
										:height="10"
										:width="10"
									/>
									<span
										style="color: #475467; font-weight: 500; font-size: 0.75rem; line-height: 1rem"
									>
										{{ value.label }}
									</span>
								</li>
							</ul>
						</td>
					</tr>
				</tbody>
			</table>

			<table style="margin-block: 1rem">
				<tbody>
					<tr>
						<td style="width: 70%" class="sm:!w-[60%]">
							<section>
								<p
									style="
										color: #475467;
										font-size: 0.75rem;
										line-height: 1rem;
										font-size: 0.75rem /* 12px */;
										letter-spacing: 0.025em;
									"
								>
									Billing to
								</p>
								<h5
									style="
										font-weight: 500;
										color: #094063;
										padding-bottom: 0.3rem;
										margin-top: 0.5rem;
										position: relative;
									"
								>
									{{ invoiceDetails?.invoice?.customer?.name }}
								</h5>
								<hr
									style="
										width: 76.93px;
										border-width: 0;
										border-bottom-width: 3px;
										border-color: #4fbfa3;
										height: 0.5rem;
										margin-bottom: 1rem;
									"
								/>

								<ul class="relative mt-3">
									<li
										v-for="(value, index) in billingInfo"
										:key="index"
										style="
											display: flex;
											align-items: center;
											max-width: 140px;
											column-gap: 0.5rem;
											padding-bottom: 0.625rem;
										"
									>
										<lazy-tools-icon
											:dir="value.icon.dir"
											:icon="value.icon.name"
											stroke="#475467"
											:height="10"
											:width="10"
										/>
										<span
											style="
												color: #475467;
												font-weight: 500;
												font-size: 0.75rem;
												line-height: 1rem;
											"
										>
											{{ value.label }}
										</span>
									</li>
								</ul>
							</section>
						</td>

						<td class="w-full">
							<section>
								<div
									style="
										display: flex;
										justify-content: space-between;
										align-items: center;
										column-gap: 1rem;
									"
								>
									<h4
										style="
											font-family: Metropolis;
											font-size: 18px;
											font-weight: 600;
											line-height: 15px;
											letter-spacing: 0em;
											text-align: left;
										"
									>
										Invoice
									</h4>

									<div
										style="
											width: 61.62px;
											height: 22.23px;
											top: 0px;
											left: 78.47px;
											padding: 8px, 13px;
											border-radius: 15.39px;
											gap: 7.69px;
											background-color:;
											font-family: Metropolis;
											font-size: 12px;
											font-weight: 500;
											line-height: 12px;
											letter-spacing: 0px;
											text-align: left;
											color:;
											text-align: center;
											line-height: 22.23px;
										"
										:style="{
											backgroundColor: invoiceStatus !== 'pending' ? '#ECFDF3' : '#fffaeb',
											color: invoiceStatus !== 'pending' ? '#12B76A' : '#F79009'
										}"
									>
										{{ invoiceStatus || 'N/A' }}
									</div>
								</div>

								<ul style="margin-top: 1.5rem">
									<li
										v-for="(item, index) in invoiceInfo"
										:key="index"
										:style="item.wrapperStyle"
										style="display: flex; column-gap: 1rem; margin-bottom: 0.5rem; color: #475467"
									>
										<span
											style="
												font-family: Metropolis;
												font-size: 12px;
												font-weight: 400;
												line-height: 12px;
												letter-spacing: 0em;
												text-align: left;
											"
											:style="item.titleStyle"
										>
											{{ index }}
										</span>
										<span
											style="
												font-family: Metropolis;
												font-size: 12px;
												font-weight: 400;
												line-height: 12px;
												letter-spacing: 0em;
												text-align: left;
											"
											:style="item.valueStyle"
										>
											{{ item.value }}
										</span>
									</li>
								</ul>
							</section>
						</td>
					</tr>
				</tbody>
			</table>

			<table
				border="0"
				class="table"
				style="position: relative; z-index: 10; border: none; width: 100%; margin-top: 0.5rem"
			>
				<thead style="width: full">
					<tr>
						<th
							v-for="(item, index) in tableColumn"
							:key="index"
							scope="col"
							style="
								min-width: 107.7px;
								height: 30.77px;
								font-weight: 500;
								background-color: #f2f4f7;
								color: #475467;
								font-size: 12px;
								opacity: 1;
							"
						>
							{{ item }}
						</th>
					</tr>
				</thead>

				<tbody>
					<tr>
						<td
							scope="row"
							style="
								background-color: white;
								height: 36.93px;
								font-weight: 500;
								font-size: 12px;
								color: #475467;
							"
							class="text-center"
						>
							{{ invoiceDetails?.invoice?.product?.name || 'N/A' }}
						</td>
						<td
							style="
								background-color: white;
								height: 36.93px;
								font-weight: 500;
								font-size: 12px;
								color: #475467;
							"
							class="text-center"
						>
							{{ invoiceDetails?.invoice?.purchase?.total_quantity || 0 }}
						</td>
						<td
							style="
								background-color: white;
								height: 36.93px;
								font-weight: 500;
								font-size: 12px;
								color: #475467;
							"
							class="text-center"
						>
							{{ currencyToNaira(invoiceDetails?.unit_cost || 0) }}
						</td>
						<td
							style="
								background-color: white;
								height: 36.93px;
								font-weight: 500;
								font-size: 12px;
								color: #475467;
							"
							class="text-center"
						>
							{{ currencyToNaira(invoiceDetails?.invoice?.amount || 0) }}
						</td>
					</tr>
					<tr>
						<td colspan="2" style="height: 36.93px"></td>
						<td
							style="
								font-family: Metropolis;
								font-size: 13px;
								font-weight: 500;
								line-height: 15px;
								letter-spacing: 0px;
								text-align: right;
								border: none;
							"
						>
							SubTotal
						</td>
						<td
							style="
								height: 36.93px;
								font-weight: 500;
								font-size: 12px;
								color: #475467;
								text-align: center;
								border: none;
							"
						>
							{{ currencyToNaira(invoiceDetails?.sub_total || 0) }}
						</td>
					</tr>
					<tr style="position: relative; border: 0">
						<td colspan="2" style="height: 36.93px; border-color: 0"></td>
						<td
							style="
								font-family: Metropolis;
								font-size: 13px;
								font-weight: 500;
								line-height: 15px;
								letter-spacing: 0.5px;
								text-align: right;
								border-color: transparent;
							"
						>
							Tax ({{ invoiceDetails?.invoice?.tax || 0 }}%)
							<hr style="position: absolute; bottom: 0; width: 40%; right: 0; border-color: #eaecf0" />
						</td>
						<td
							style="
								height: 36.93px;
								font-weight: 500;
								font-size: 12px;
								color: #475467;
								text-align: center;
								border-color: transparent;
							"
						>
							{{ currencyToNaira(invoiceDetails?.tax_percentage_amount || 0) }}
						</td>
					</tr>
				</tbody>
			</table>

			<table style="margin-top: 2.5rem">
				<tbody>
					<tr>
						<td style="width: 80%" class="sm:w-full">
							<section>
								<h5
									style="
										font-weight: 500;
										color: #094063;
										padding-bottom: 0.3rem;
										margin-top: 0.5rem;
										position: relative;
									"
								>
									Payment Method
								</h5>
								<ul style="margin-top: 0.5rem">
									<li
										v-for="(item, index) in paymentOption"
										:key="index"
										style="display: flex; column-gap: 1rem; margin-bottom: 0.5rem; color: #475467"
									>
										<span
											style="
												font-family: Metropolis;
												font-size: 12px;
												font-weight: 400;
												line-height: 12px;
												letter-spacing: 0em;
												text-align: left;
											"
											:style="item.titleStyle"
										>
											{{ index }}
										</span>
										<span
											style="
												font-family: Metropolis;
												font-size: 12px;
												font-weight: 400;
												line-height: 12px;
												letter-spacing: 0em;
												text-align: left;
											"
											:style="item.valueStyle"
										>
											{{ item.value }}
										</span>
									</li>
								</ul>
							</section>
						</td>

						<td class="w-full">
							<section class="w-full">
								<div
									style="
										display: flex;
										justify-content: space-between;
										align-items: center;
										column-gap: 1rem;
										min-width: 180px;
										position: relative;
										text-align: center;
										flex-direction: column;
										position: relative;
									"
								>
									<img
										src="@/assets/images/png/signature.png"
										alt="signature"
										height="38.46px"
										width="80px"
									/>
									<hr style="border: none; border-bottom: 3px solid #4fbfa3; width: 100%" />
									<p
										style="
											padding-top: 0.5rem;
											font-size: 12px;
											font-weight: 400;
											line-height: 15px;
											letter-spacing: 0px;
											text-align: left;
											color: #475467;
											letter-spacing: 0.5px;
										"
									>
										Signature
									</p>
								</div>
							</section>
						</td>
					</tr>
				</tbody>
			</table>

			<section style="margin-top: 2rem">
				<h5
					style="
						font-weight: 500;
						color: #094063;
						padding-bottom: 0.3rem;
						margin-top: 0.5rem;
						position: relative;
					"
				>
					Terms & Conditions
				</h5>
				<ul style="margin-top: 0.5rem">
					<li
						v-for="(item, index) in termsAndConditions"
						:key="index"
						style="display: flex; column-gap: 1rem; margin-bottom: 0.5rem; color: #475467"
					>
						<span
							style="
								font-family: Metropolis;
								font-size: 12px;
								font-weight: 400;
								line-height: 12px;
								letter-spacing: 0em;
								text-align: left;
							"
						>
							{{ item }}
						</span>
					</li>
				</ul>
			</section>

			<img
				src="@/assets/svg/icons/fill/green-shaded-mcg-cap.svg"
				alt="image placeholder"
				style="position: absolute; top: -1rem; height: 100%; left: 0; width: 100%; object-fit: contain"
			/>
		</div>
	</div>
</template>
